import { Component, OnInit, ViewChild } from '@angular/core';
import {
  GridDataResult,
  PagerSettings,
  DataStateChangeEvent,
} from '@progress/kendo-angular-grid';
import {
  LoaderService,
  MessageService,
  ColumnOptions,
  GridState,
  DataGridComponent,
  GridStateCompositeFilterDescriptor,
  ValidationResultService,
  NamedEntity,
} from '@shared';
import { CategoryService } from '../../services';
import { GridRowCategory, GridRowItemExtended, ItemExtend } from '../../models';
import { Category } from '../../models/category.model';
import { GlobalEventsService, ItemService } from '@shared-data';

@Component({
  selector: 'app-category',
  templateUrl: './category.component.html',
  styleUrls: ['./category.component.scss'],
})
export class CategoryComponent implements OnInit {
  categories: GridDataResult = { data: [], total: 0 };
  assignedItems: GridDataResult = { data: [], total: 0 };
  unAssignedItems: GridDataResult = { data: [], total: 0 };
  pageSize = 10;
  state = new GridState(this.pageSize);
  itemState: GridState = new GridState(this.pageSize);
  addItemState: GridState = new GridState(8);
  itemColumns: ColumnOptions[] = [];
  categoryColumns: ColumnOptions[] = [];
  pagerSettings: PagerSettings = {
    buttonCount: 3,
    info: true,
    type: 'numeric',
    previousNext: true,
  };
  selectedCategory: Category | null = null;
  fetchType: string = 'assign';
  isEditMode = false;
  showUnAssignDialog: boolean = false;
  selectedUnassignItem: any;
  unAssignDialogText: string = '';
  showAssignDialog: boolean = false;
  showIsActiveCategoryUpdate: boolean = false;
  updateCategoryMessage: string = '';
  updateCategory!: Category;
  isSaveEnabled: boolean = false;
  showSortOrderSavePopup: boolean = false;
  showCategorySortOrderSavePopup: boolean = false;
  selectItemRow: any;
  selectCategoryRow: any;
  isCategoryEditMode: boolean = false;
  showAddCategoryPopup: boolean = false;
  addCategory!: any;
  showDeleteCategoryPopup: boolean = false;
  deleteCategoryId: any;
  oldValue: any;
  validationPassed: boolean = true;

  itemsToAssign: Set<number> = new Set<number>();
  selectedConcept: NamedEntity | null = null;
  @ViewChild('itemGrid') itemGrid!: DataGridComponent;
  @ViewChild('categoriesGrid') categoriesGrid!: DataGridComponent;

  constructor(
    private categoryService: CategoryService,
    private itemService: ItemService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private validationResultService: ValidationResultService,
    private globalEventsService: GlobalEventsService,
  ) {
    this.categoryColumns = new GridRowCategory().getColumns();
    this.itemColumns = new GridRowItemExtended().getColumns();
  }

  ngOnInit(): void {
    this.subscribeToConceptChanges();
  }

  subscribeToConceptChanges() {
    this.globalEventsService.selectedConcept$.subscribe((concept) => {
        this.selectedCategory = null;
        this.selectedConcept = concept;
        this.setDefaultState();
    });
  }

  onDataStateChanged(event: DataStateChangeEvent): void {
    this.state = event as GridState;
    this.fetchCategories(event as GridState);
  }

  onDataStateChangedItems(event: DataStateChangeEvent): void {
    this.itemState = event as GridState;
    this.fetchItems(this.itemState);
  }

  onDataStateChangedAddItems(event: DataStateChangeEvent): void {
    this.addItemState = event as GridState;
    this.fetchItems(this.addItemState);
  }

  setDefaultState(): void {
    this.state = new GridState(this.pageSize);
    this.state.filter = this.state.filter?.and(
      { field: 'conceptKey', operator: 'eq', value: this.selectedConcept?.id }
    )
    this.fetchCategories(this.state);
  }

  handleRowClick(event: any): void {
    this.fetchType = 'assign';
    this.selectedCategory = event.selectedRows[0].dataItem;
    this.isEditMode = false;
    this.fetchItems(this.itemState);
    this.selectCategoryRow = undefined;
  }

  fetchCategories(state: GridState, highlightItemId?: any): void {
    this.loaderService.show();
    this.state.skip = undefined;
    this.state.take = undefined;
    if (state.sort?.length) this.state.sort = state.sort;
    state.sort = [{ field: 'sortOrder', dir: 'asc' }];

    this.categoryService.getAllCategories(this.state).subscribe({
      next: (result: GridDataResult) => {
        this.categories = result;
        this.categories.data = this.categories.data.map(
          (cat: Category) => new GridRowCategory(cat),
        );
        this.assignedItems = { data: [], total: 0 };
        this.loaderService.hide();
        this.fetchItems(this.itemState, highlightItemId);
        this.selectCategoryRow = this.selectedCategory?.id;
        if (this.selectedCategory) {
          this.selectCategoryRow = this.selectedCategory.id;
          this.categoriesGrid.scrollToRow(this.selectCategoryRow);
        } else {
          this.selectCategoryRow = undefined;
        }
      },
      error: () => {
        this.messageService.error('Failed to load categories');
        this.loaderService.hide();
      },
    });
  }

  fetchItems(state: GridState, isHighliteId?: any): void {
    if (this.fetchType === 'assign' && this.selectedCategory === null) {
      return;
    }
    this.loaderService.show();
    if (this.fetchType === 'assign') {
      this.itemState.take = undefined;
      this.itemState.skip = undefined;
    } else {
      if (state.skip !== undefined) this.addItemState.skip = state.skip;
      if (state.take !== undefined) this.addItemState.take = state.take;
    }
    this.selectItemRow = undefined;
    state.filter = new GridStateCompositeFilterDescriptor().or({
      field: 'categoryId',
      operator: 'eq',
      value:
        this.fetchType === 'unassign'
          ? null
          : (this.selectedCategory?.id ?? null),
    });

    if (!state.sort || state.sort.length === 0) {
      state.sort = [{ field: 'sortOrder', dir: 'asc' }];
    }

    this.itemService.getAllItems(state, false).subscribe({
      next: (result: GridDataResult) => {
        this.loaderService.hide();
        if (this.fetchType === 'assign') {
          this.assignedItems = result;
          this.assignedItems.data = this.assignedItems.data.map(
            (item: ItemExtend) => new GridRowItemExtended(item),
          );
        } else {
          this.unAssignedItems = result;
          this.unAssignedItems.data = this.unAssignedItems.data.map(
            (item: ItemExtend) => new GridRowItemExtended(item),
          );
        }
        this.isSaveEnabled = false;
        if (isHighliteId) {
          this.selectItemRow = isHighliteId[0];
          this.itemGrid.scrollToRow(this.selectItemRow);
        }
      },
      error: () => {
        this.messageService.error('Failed to load items');
        this.loaderService.hide();
      },
    });
  }
  onClickUnAssign(dataItem: any): void {
    this.selectedUnassignItem = dataItem;
    this.unAssignDialogText = `Do you want to unassign ${this.selectedUnassignItem.itemName} from ${this.selectedCategory?.name} ?`;
    this.showUnAssignDialog = true;
  }

  confirmUnassign(): void {
    this.showUnAssignDialog = false;
    if (!this.selectedCategory?.id) {
      return;
    }
    this.itemService.unassignItem(this.selectedUnassignItem.id).subscribe({
      next: () => {
        this.messageService.success(
          `Unassigned "${this.selectedUnassignItem.itemName}" successfully.`,
        );
        this.fetchType = 'assign';
        this.fetchCategories(this.state);
      },
      error: (error) => {
        this.messageService.error(
          `Failed to unassign "${this.selectedUnassignItem.itemName}".`,
        );
      },
    });
  }

  onClickAddItems(): void {
    this.loaderService.show();
    const state = new GridState(10, [{ field: 'itemName', dir: 'asc' }]);
    state.filter = new GridStateCompositeFilterDescriptor().and({
      field: 'categoryId',
      operator: 'isnull',
    });
    this.itemService.getAllItems(state, false).subscribe({
      next: (result: GridDataResult) => {
        result.data = result.data.map(
          (item: ItemExtend) => new GridRowItemExtended(item),
        );

        this.unAssignedItems = result;
        this.showAssignDialog = true;
        this.loaderService.hide();
      },
      error: (err) => {
        var validationError = err?.error;

        if (validationError) {
          this.validationResultService.show(validationError);
        }

        this.messageService.error('Failed to load unassigned items');
        this.loaderService.hide();
      },
    });
  }

  confirmAssign() {
    this.showAssignDialog = false;
    if (this.itemsToAssign.size === 0) {
      this.messageService.error('Please add at least one item.');
      return;
    }
    this.loaderService.show();
    this.itemService
      .assignItems(Array.from(this.itemsToAssign), this.selectedCategory?.id)
      .subscribe({
        next: (res) => {
          this.messageService.success(`Items Assigned Successfully.`);
          this.fetchType = 'assign';
          this.fetchCategories(this.state, res);
          this.loaderService.hide();
        },
        error: (error) => {
          var validationError = error?.error;

          if (validationError) {
            this.validationResultService.show(validationError);
          }
          this.messageService.error(`Failed to Assign Items.`);
          this.loaderService.hide();
        },
      });
  }

  onBooleanValueChange(row: any) {
    this.updateCategory = row;
    const message =
      this.updateCategory.isActive == true ? 'Active' : 'In Active';
    this.updateCategoryMessage = `Do you want to ${message} ${this.updateCategory.name} ?`;
    this.showIsActiveCategoryUpdate = true;
  }

  confirmCategoryUpdate() {
    this.showIsActiveCategoryUpdate = false;
    this.categoryService.updateCategory(this.updateCategory).subscribe({
      next: (res) => {
        this.messageService.success(`Category Updated Successfully.`);
        this.selectedCategory = null;
        this.fetchCategories(this.state);
      },
      error: (error) => {
        this.messageService.error(`Failed to update category.`);
      },
    });
  }

  closeUpdateCategoryPopup() {
    this.showIsActiveCategoryUpdate = false;
    this.fetchCategories(this.state);
  }

  onClickSave(): void {
    if (!this.validationPassed) {
      return;
    }
    this.isEditMode = false;
    this.showSortOrderSavePopup = true;
  }

  SaveItems(): void {
    this.closeItemUpdatePopup();
    this.loaderService.show();
    this.itemService.batchUpdateItems(this.assignedItems.data).subscribe({
      next: (res) => {
        this.loaderService.hide();
        this.messageService.success('Items updated successfully.');
        this.fetchItems(this.itemState, res?.id);
      },
      error: (err) => {
        this.loaderService.hide();
        this.messageService.error('Failed to Update the Items.');
      },
    });
  }

  closeItemUpdatePopup() {
    this.showSortOrderSavePopup = false;
  }

  closeCategoryAddPopup() {
    this.showAddCategoryPopup = false;
  }

  onEditClick(): void {
    this.isEditMode = true;
  }

  onCancelClick(): void {
    this.isEditMode = false;
    this.fetchItems(this.itemState);
  }

  blockInvalidKeys(event: KeyboardEvent): void {
    const invalidKeys = ['-', '+', 'e', 'E'];
    const input = event.target as HTMLInputElement;

    if (invalidKeys.includes(event.key)) {
      event.preventDefault();
      return;
    }

    const isFirstChar = input.selectionStart === 0;
    if (event.key === '0' && isFirstChar) {
      event.preventDefault();
    }
  }

  onEnterKey(row: any, numericBox: any): void {
    const inputEl = numericBox?.numericInput?.nativeElement as HTMLInputElement;
    inputEl?.blur();
    setTimeout(() => {
      this.sortOrderChange(this.categoriesGrid, row, 'categories');
    });
  }

  onKeyEnterItem(row: any, numericBox: any): void {
    const inputEl = numericBox?.numericInput?.nativeElement as HTMLInputElement;
    inputEl?.blur();
    setTimeout(() => {
      this.sortOrderChange(this.itemGrid, row, 'items');
    });
  }

  onEditCategories(): void {
    this.isCategoryEditMode = true;
  }

  onCancelCategories(): void {
    this.isCategoryEditMode = false;
    this.fetchCategories(this.state, this.selectedCategory?.id);
  }

  onSaveCategories(): void {
    if (!this.validationPassed) {
      return;
    }
    this.isCategoryEditMode = false;
    this.showCategorySortOrderSavePopup = true;
  }

  onClickAddCategory(): void {
    this.showAddCategoryPopup = true;
    this.addCategory = new Category({ sortOrder: this.categories.total + 1 });
  }

  closeCategoryDeletePopup(): void {
    this.showDeleteCategoryPopup = false;
  }

  AddCategory(): void {
    if (!this.addCategory?.name) {
      this.messageService.error('Please Enter Category');
      return;
    }
    this.closeCategoryAddPopup();
    this.loaderService.show();
    this.categoryService.createCategory(this.addCategory).subscribe({
      next: (res) => {
        this.loaderService.hide();
        this.messageService.success('Category Added successfully.');
        this.selectedCategory = res;
        this.fetchCategories(this.state);
        this.addCategory = null;
      },
      error: (err) => {
        this.loaderService.hide();
        const validationError = err?.error;
        if (validationError) {
          this.validationResultService.show(validationError);
        }
        this.messageService.error('Failed to Add Category.');
        this.addCategory = null;
      },
    });
  }

  onDeleteCategory(category: any): void {
    this.showDeleteCategoryPopup = true;
    this.deleteCategoryId = category.id;
  }

  delteCategory(): void {
    this.closeCategoryDeletePopup();
    this.categoryService.deleteCategory(this.deleteCategoryId).subscribe({
      next: (res) => {
        this.messageService.success('Category Deleted successfully.');
        this.fetchCategories(this.state);
      },
      error: (err) => {
        this.messageService.error('Failed to Delete Category.');
      },
    });
  }

  swapRows(
    dataGrid: any,
    row: any,
    direction: 'up' | 'down',
    swapIn: String,
  ): void {
    var dataList = dataGrid.dataItems.data;
    let rowId = row?.id;
    const index1 = dataList.findIndex((item: any) => item.id === rowId);
    if (
      (index1 === 0 && direction === 'up') ||
      (index1 === dataList.length - 1 && direction === 'down')
    ) {
      return;
    }
    const index2 = direction === 'up' ? index1 - 1 : index1 + 1;
    [dataList[index1], dataList[index2]] = [dataList[index2], dataList[index1]];
    const tempSort = dataList[index1].sortOrder;
    dataList[index1].sortOrder = dataList[index2].sortOrder;
    dataList[index2].sortOrder = tempSort;

    if (swapIn === 'categories') {
      this.selectCategoryRow = rowId;
      this.selectedCategory = row;
      this.fetchItems(this.itemState);
    } else {
      this.selectItemRow = rowId;
    }
  }

  sortOrderChange(dataGrid: any, row: any, sortIn: string): void {
    this.validationPassed = true;
    var dataList = dataGrid.dataItems.data;
    let rowId = row.id;

    const fromIndex = dataList.findIndex((item: any) => item.id === rowId);
    const toIndex = row.sortOrder - 1;
    const value = row.sortOrder;
    const maxValue = dataList.length;

    if (value === 0 || value > maxValue) {
      this.messageService.error(`value should be between 1 to ${maxValue}`);
      dataList[fromIndex].sortOrder = this.oldValue;
      this.validationPassed = false;
      return;
    }

    const item = dataList.splice(fromIndex, 1)[0];
    dataList.splice(toIndex, 0, item);

    dataList.forEach((item: any, index: number) => {
      item.sortOrder = index + 1;
    });

    this.isSaveEnabled = true;
    sortIn === 'items'
      ? (this.selectItemRow = rowId)
      : (this.selectCategoryRow = rowId);
    dataGrid.scrollToRow(rowId);
  }

  closeCategorySortOrderPopup() {
    this.showCategorySortOrderSavePopup = false;
  }
  saveCategorySortOrder(): void {
    this.loaderService.show();
    this.categoryService.batchUpdateCategories(this.categories.data).subscribe({
      next: (res) => {
        this.messageService.success(
          'Categories Sort order updated successfully.',
        );
        this.fetchCategories(this.state);
        this.closeCategorySortOrderPopup();
        this.loaderService.hide();
      },
      error: (err) => {
        var validationError = err?.error;

        if (validationError) {
          this.validationResultService.show(validationError);
        }

        this.loaderService.hide();
        this.messageService.error('Failed to update the Category Sort Order.');
      },
    });
  }
}
